import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaEye,
  FaTrash,
  FaCalendarAlt
} from "react-icons/fa";
import "./PackageRequests.css";

// Helper component for status badges
const StatusBadge = ({ status }) => (
  <span className={`status-badge ${status?.toLowerCase() || 'pending'}`}>
    {status || 'pending'}
  </span>
);

const PackageRequests = () => {
  // State for all requests, search, filters, and UI
  const [allRequests, setAllRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Real-time listener for package requests
  useEffect(() => {
    const requestsCollectionRef = collection(db, "package_request");
    // Corrected: Order by the 'request_date' field from your database
    const q = query(requestsCollectionRef, orderBy("request_date", "desc"));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const requestList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllRequests(requestList);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching requests:", err);
      setError("Failed to listen to package requests.");
      setLoading(false);
    });

    // Cleanup listener on component unmount
    return () => unsubscribe();
  }, []);

  // Memoized filtering and searching
  const filteredRequests = useMemo(() => {
    return allRequests.filter((req) => {
      const lowercasedTerm = searchTerm.toLowerCase();
      // Corrected: Use 'customer_name' and 'package_name' for searching
      const matchesSearch =
        !searchTerm ||
        req.customer_name?.toLowerCase().includes(lowercasedTerm) ||
        req.package_name?.toLowerCase().includes(lowercasedTerm);
      
      // Corrected: Use 'status' for filtering
      const matchesStatus =
        statusFilter === "all" || req.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [allRequests, searchTerm, statusFilter]);

  // Handle approving a request
  const handleApprove = async (request) => {
    if (!window.confirm("Are you sure you want to approve this request? It will be moved to user subscriptions.")) {
        return;
    }
    
    // Corrected: Use 'customer_id' to create the new document reference
    const userSubscriptionRef = doc(db, "user_subscriptions", request.customer_id);
    const packageRequestRef = doc(db, "package_request", request.id);

    try {
      // Corrected: Map fields from the request object to the new subscription data
      const subscriberData = {
        customerId: request.customer_id,
        customerName: request.customer_name,
        packageName: request.package_name,
        subscriptionStartDate: Timestamp.now(),
        originalRequestId: request.id,
        status: 'active'
        // Add any other fields you need to transfer
      };
      
      // 1. Create the new subscription document
      await setDoc(userSubscriptionRef, subscriberData);
      
      // 2. Delete the original package request
      await deleteDoc(packageRequestRef);

      alert("Request approved and subscription created!");

    } catch (error) {
      console.error("Error approving request: ", error);
      alert(`Error approving request: ${error.message}`);
    }
  };

  // Handle rejecting a request
  const handleReject = async (request) => {
    const requestDocRef = doc(db, "package_request", request.id);
    try {
        // Corrected: Update the 'status' field
        await updateDoc(requestDocRef, { status: 'rejected' });
        alert("Request has been rejected.");
    } catch (error) {
        console.error("Error rejecting request: ", error);
        alert(`Error rejecting request: ${error.message}`);
    }
  };
  
  // Handle deleting a request permanently
  const handleDelete = async (requestId) => {
    if (window.confirm("Are you sure you want to permanently delete this request?")) {
      try {
        await deleteDoc(doc(db, "package_request", requestId));
        alert("Request deleted successfully!");
      } catch (error) {
        console.error("Error deleting request:", error);
        alert(`Error deleting request: ${error.message}`);
      }
    }
  };

  // Correctly format Firestore Timestamp
  const formatDate = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return "Invalid date";
    return timestamp.toDate().toLocaleDateString("en-US", {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const viewRequestDetails = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
  };
  
  // Pagination logic
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentRequests = filteredRequests.slice(startIndex, startIndex + itemsPerPage);
  const goToPage = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;
  if (error) return <div className="error-container">{error}</div>;

  return (
    <div className="requests-container">
        <h1>Package Subscription Requests</h1>
        <div className="controls-section">
            <div className="search-container">
                <FaSearch className="search-icon" />
                <input type="text" placeholder="Search by name, email, package..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input"/>
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                {/* Corrected: The status in your DB is 'approved', so added it here. If you reject, it will be 'rejected' */}
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
            </select>
        </div>
        <div className="table-container">
            <table className="requests-table">
                <thead>
                    <tr>
                        <th>Customer Name</th>
                        <th>Package</th>
                        <th>Request Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentRequests.length === 0 ? (
                    <tr><td colSpan="5" className="no-data">No requests match the current filters.</td></tr>
                    ) : (
                    currentRequests.map((req) => (
                        <tr key={req.id}>
                            {/* Corrected: Display data using the correct field names */}
                            <td>{req.customer_name}</td>
                            <td>{req.package_name || "N/A"}</td>
                            <td>{formatDate(req.request_date)}</td>
                            <td><StatusBadge status={req.status} /></td>
                            <td>
                                <div className="action-buttons">
                                    <button onClick={() => viewRequestDetails(req)} title="View Details"><FaEye /></button>
                                    {/* Corrected: Check the 'status' field */}
                                    {req.status !== 'approved' && req.status !== 'rejected' && (
                                        <>
                                        <button onClick={() => handleApprove(req)} title="Approve Request"><FaCheckCircle /></button>
                                        <button onClick={() => handleReject(req)} title="Reject Request"><FaTimesCircle /></button>
                                        </>
                                    )}
                                    <button onClick={() => handleDelete(req.id)} title="Delete Request"><FaTrash /></button>
                                </div>
                            </td>
                        </tr>
                    ))
                    )}
                </tbody>
            </table>
        </div>
        {totalPages > 1 && (
            <div className="pagination">
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}><FaChevronLeft /></button>
                <span>Page {currentPage} of {totalPages}</span>
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}><FaChevronRight /></button>
            </div>
        )}
        {showModal && selectedRequest && (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Request Details</h2>
                    <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    {/* Corrected: Display details using correct field names */}
                    <div className="detail-item"><label>Customer ID:</label><span>{selectedRequest.customer_id}</span></div>
                    <div className="detail-item"><label>Customer Name:</label><span>{selectedRequest.customer_name}</span></div>
                    <hr/>
                    <div className="detail-item"><label>Package:</label><span>{selectedRequest.package_name}</span></div>
                    <hr/>
                    <div className="detail-item"><strong><FaCalendarAlt /> Request Date:</strong> <span>{formatDate(selectedRequest.request_date)}</span></div>
                    <div className="detail-item"><label>Status:</label><StatusBadge status={selectedRequest.status} /></div>
                </div>
            </div>
            </div>
        )}
    </div>
  );
};

export default PackageRequests;